<template>
  <!-- Try it: log a month against a 30 h hour bank and see the cycle card answer. The maths and
       words are the app's (RetainerFacts: hours left = bank − approved; "{hours} h over if
       pending time is approved", "That's {amount} on the {date} invoice", "{amount} in extra
       hours on the next invoice", "Tracked, not billed (this retainer's setting)"; Extra hours
       "Bill on the next invoice" / "Track only"). The bank, rate and dates are sample data. Every
       line is always drawn, so nothing moves. -->
  <div class="rp sdh">
    <div class="sdh-controls">
      <div class="sdh-ctl">
        <span :id="`${uid}-a`" class="sdh-label">{{ chapter.approvedLabel }}</span>
        <div class="sdh-seg" role="group" :aria-labelledby="`${uid}-a`">
          <button v-for="h in approvedSteps" :key="h" type="button" :aria-pressed="approved === h" :class="{ 'is-on': approved === h }" @click="approved = h">
            <span class="rp-num">{{ h }} h</span>
          </button>
        </div>
      </div>
      <div class="sdh-ctl">
        <span :id="`${uid}-p`" class="sdh-label">{{ chapter.pendingLabel }}</span>
        <div class="sdh-seg" role="group" :aria-labelledby="`${uid}-p`">
          <button v-for="h in pendingSteps" :key="h" type="button" :aria-pressed="pending === h" :class="{ 'is-on': pending === h }" @click="pending = h">
            <span class="rp-num">{{ h }} h</span>
          </button>
        </div>
      </div>
      <div class="sdh-ctl">
        <span :id="`${uid}-x`" class="sdh-label">Extra hours</span>
        <div class="sdh-seg sdh-seg--2" role="group" :aria-labelledby="`${uid}-x`">
          <button type="button" :aria-pressed="bills" :class="{ 'is-on': bills }" @click="bills = true">Bill on the next invoice</button>
          <button type="button" :aria-pressed="!bills" :class="{ 'is-on': !bills }" @click="bills = false">Track only</button>
        </div>
      </div>
    </div>

    <section class="sdh-card" aria-live="polite">
      <div class="sdh-cycle rp-num" translate="no"><b>This cycle · Oct 1 – Oct 31</b><small>9 days left</small></div>
      <strong class="sdh-head rp-num" :class="{ 'is-over': isOver }" translate="no">{{ isOver ? `${fmt(overHours)} h over` : `${fmt(left)} h left` }}</strong>
      <small class="sdh-sub rp-num" translate="no">{{ pending ? `${fmt(approved)} of ${BANK} h approved · ${fmt(pending)} h waiting for approval` : `${fmt(approved)} of ${BANK} h approved` }}</small>
      <div class="sdh-meter" aria-hidden="true">
        <i class="sdh-a" :style="{ width: `${(Math.min(approved, BANK) / BANK) * 100}%` }" />
        <i class="sdh-p" :style="{ width: `${(Math.min(pending, Math.max(0, BANK - approved)) / BANK) * 100}%` }" />
      </div>
      <div class="sdh-warn rp-num" translate="no">
        <b v-if="warnTitle">{{ warnTitle }}</b>
        <small v-if="warnSub">{{ warnSub }}</small>
      </div>
    </section>
  </div>
</template>

<script setup>
const props = defineProps({
  chapter: { type: Object, required: true },
});
const uid = useId();
const BANK = 30;
const RATE = 150;
const approvedSteps = [12, 24, 30, 33];
const pendingSteps = [0, 4, 8];
const approved = ref(24);
const pending = ref(8);
const bills = ref(true);

const left = computed(() => Math.max(0, BANK - approved.value));
const overHours = computed(() => Math.max(0, approved.value - BANK));
const isOver = computed(() => overHours.value > 0);
const pendingOver = computed(() => Math.max(0, approved.value + pending.value - BANK) - overHours.value);
const fmt = (h) => (Number.isInteger(h) ? String(h) : h.toFixed(1));
const money = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The same order the app's cycle card uses: extra already over, else over once pending is approved.
const warnTitle = computed(() => {
  if (isOver.value && bills.value) return `${money(overHours.value * RATE)} in extra hours on the next invoice`;
  if (pendingOver.value > 0) return `${fmt(pendingOver.value)} h over if pending time is approved`;
  return "";
});
const warnSub = computed(() => {
  if (isOver.value && bills.value) return "The Nov 1 invoice";
  if (pendingOver.value > 0) return bills.value ? `That's ${money(pendingOver.value * RATE)} on the Nov 1 invoice` : "Tracked, not billed (this retainer's setting)";
  return "";
});
</script>

<style scoped>
.sdh { display: grid; gap: 12px; max-width: 560px; margin: 0 auto; padding: 14px; }
.sdh-controls { display: grid; gap: 10px; }
.sdh-ctl { display: grid; gap: 5px; }
.sdh-label { color: var(--rp-muted); font-size: 11.5px; font-weight: 600; }
.sdh-seg { display: grid; grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); gap: 3px; border-radius: 8px; background: var(--rp-grey-fill); padding: 3px; }
.sdh-seg button { min-height: 44px; border: 0; border-radius: 6px; background: transparent; padding: 4px 6px; color: var(--rp-muted); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
.sdh-seg button:hover { color: var(--rp-text); }
.sdh-seg button:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.sdh-seg button.is-on { background: var(--rp-card); color: var(--rp-text); box-shadow: 0 1px 2px rgb(0 0 0 / 0.14); }
.sdh-card { display: grid; gap: 4px; border: 1px solid var(--rp-line); border-radius: 10px; background: var(--rp-card); padding: 12px 14px; }
.sdh-cycle { display: flex; justify-content: space-between; gap: 8px; font-size: 12.5px; }
.sdh-cycle small { color: var(--rp-muted); font-size: 11.5px; }
.sdh-head { margin-top: 6px; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.15; }
.sdh-head.is-over { color: var(--rp-red); }
.sdh-sub { color: var(--rp-muted); font-size: 11.5px; }
.sdh-meter { display: flex; height: 10px; margin: 8px 0 4px; overflow: hidden; border-radius: 4px; background: var(--rp-grey-fill); }
.sdh-meter i { height: 100%; transition: width 0.4s cubic-bezier(0.33, 1, 0.68, 1); }
.sdh-a { background: #2f5bd3; }
.sdh-p { background: repeating-linear-gradient(135deg, #8aa4ec 0 4px, #b9c8f3 4px 8px); }
.dark .sdh-a { background: #93aef9; }
.dark .sdh-p { background: repeating-linear-gradient(135deg, #5d73b5 0 4px, #46598e 4px 8px); }
.sdh-warn { display: grid; gap: 1px; min-height: 2.9em; font-size: 12px; }
.sdh-warn b { color: var(--rp-amber); font-weight: 600; }
.sdh-warn small { color: var(--rp-muted); font-size: 11.5px; }
@media (min-width: 720px) { .sdh { grid-template-columns: 1fr 1fr; align-items: start; } }
@media (prefers-reduced-motion: reduce) { .sdh * { transition: none !important; } }
</style>
